import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

import List from "../../../../../components/List";
import { LeaveTypeInfoPanel } from "./LeaveTypeInfoPanel";
import { LeaveTypeCreateEdit } from "./LeaveTypeCreateEdit";
import { useLeaveTypes } from "../../../../../hooks/query/useLeaveTypes";
import { ILeaveType } from "../../../../../interfaces/LeaveInterface";
import { LeaveAccrualFrequency, LeaveAccrualMethod } from "../../../../../constants/leave";
import { EmptyView } from "../../../../../components/OList";

// eslint-disable-next-line import/prefer-default-export
export function LeaveTypesList(): React.ReactNode {
  const { data: leaveTypes = [], isLoading } = useLeaveTypes();
  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<ILeaveType | null>(null);

  const handleSuccess = () => {
    setShowCreateEditPanel(false);
  };

  return (
    <Box height="100%">
      <List<ILeaveType>
        automation="leave-type-list"
        data={leaveTypes}
        isLoading={isLoading}
        onCreateNew={() => setShowCreateEditPanel(true)}
        createLabel="Add Leave Type"
        isResponsive
        activeRow={selectedLeave?.id}
        withoutSearch
        onRowClick={(row) => {
          setSelectedLeave(selectedLeave?.id === row.id ? null : (row as ILeaveType));
        }}
        columns={[
          {
            key: "name",
            label: "Name",
            sortable: true,
            formatter: (row: ILeaveType) => (
              <Typography variant="body2" fontWeight={500}>
                {row.name}
              </Typography>
            )
          },
          {
            key: "code",
            label: "Code",
            sortable: true,
            formatter: (row: ILeaveType) => <Typography variant="body2">{row.code}</Typography>
          },
          {
            key: "quota",
            label: "Quota (Days)",
            formatter: (row: ILeaveType) => <Typography variant="body2">{row.quota}</Typography>
          },
          {
            key: "accrualFrequency",
            label: "Accrual Period",
            formatter: (row: ILeaveType) => (
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                {row.accrualFrequency === LeaveAccrualFrequency.MONTHLY ? "Monthly" : "Yearly"}
              </Typography>
            )
          },
          {
            key: "accrualMethod",
            label: "Accrual Method",
            formatter: (row: ILeaveType) => (
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                {row.accrualMethod === LeaveAccrualMethod.LUMP_SUM ? "Lump Sum" : "Accrual"}
              </Typography>
            )
          },
          {
            key: "isPaid",
            label: "Paid",
            formatter: (row: ILeaveType) => (
              <Typography
                variant="body2"
                sx={{ color: row.isPaid ? "success.main" : "text.secondary" }}
              >
                {row.isPaid ? "Yes" : "No"}
              </Typography>
            )
          },
          {
            key: "isCarryable",
            label: "Carryable",
            formatter: (row: ILeaveType) => (
              <Typography
                variant="body2"
                sx={{ color: row.isCarryable ? "success.main" : "text.secondary" }}
              >
                {row.isCarryable ? "Yes" : "No"}
              </Typography>
            )
          }
        ]}
      >
        <EmptyView>
          <Typography component="div" variant="body2">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "350px",
                width: "100%"
              }}
            >
              <Typography component="div">
                <Box textAlign="center" marginTop="16px">
                  No leave types records found
                </Box>
              </Typography>
            </Box>
          </Typography>
        </EmptyView>
      </List>

      {selectedLeave && (
        <LeaveTypeInfoPanel
          leave={selectedLeave}
          onClose={() => setSelectedLeave(null)}
          onEdit={() => setShowCreateEditPanel(true)}
        />
      )}

      {showCreateEditPanel && (
        <LeaveTypeCreateEdit
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedLeave(null);
          }}
          leave={selectedLeave}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
}
